import React, { useState, useEffect } from 'react';
import Joyride, { STATUS } from 'react-joyride';
import { useTheme } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';

const MobileTutorial = ({ onFinish }) => {
  const theme = useTheme();

  const tutorialSteps = [
    {
      target: 'body',
      content: (
        <div style={{ 
          maxHeight: '90vh',
          overflowY: 'auto',
          width: '100%',
          padding: theme.spacing(2)
        }}>
          <h3 style={{ 
            color: theme.palette.primary.main, 
            marginTop: 0,
            marginBottom: theme.spacing(2)
          }}>
            Welcome to Writellect! 🎉
          </h3>
          <p style={{ marginBottom: theme.spacing(2) }}>
            You've been granted <strong>20 credits</strong> to start creating amazing content! With these credits, you can generate:
          </p>
          <ul style={{ marginBottom: theme.spacing(2), paddingLeft: theme.spacing(2) }}>
            <li>Up to ~133,000 words using GPT-4o-mini model</li>
            <li>Or ~20 images using Flux Pro 1.1 model</li>
          </ul>
          <p>During the free trial, you have access to non-premium models. For premium models(like Claude 3.5 Sonnet), you need to subscribe to a plan.</p>
          <p>
            Let's take a quick tour of the platform to help you get started.
          </p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
      styles: {
        options: {
          width: '100vw',
          arrowColor: 'transparent'
        },
        tooltip: {
          width: '100vw',
          maxWidth: '100vw',
          minHeight: 'min-content',
          borderRadius: 0,
          padding: 0,
          backgroundColor: theme.palette.background.paper
        }
      }
    },
    {
      target: '.project-selector',
      content: 'Select or create a project to manage your content. Each project can have its own resources, articles, and social media posts.',
      disableBeacon: true,
      placement: 'bottom'
    },
    {
      target: '.dashboard-link',
      content: 'The dashboard gives you an overview of your project\'s performance and recent activity.',
      placement: 'right'
    },
    {
      target: '[data-id="sources"]',
      content: 'The Sources section is where you manage your content sources and data streams.',
      placement: 'right',
      spotlightPadding: 5,
      disableBeacon: true
    },
    {
      target: '.articles-section',
      content: 'Create and manage your blog articles here.',
      placement: 'right'
    },
    {
      target: '.settings-section',
      content: 'Configure your project settings, integrations, and preferences.',
      placement: 'right'
    }
  ];

  return (
    <Joyride
      steps={tutorialSteps}
      run={true}
      continuous
      showProgress
      showSkipButton
      callback={(data) => {
        if ([STATUS.FINISHED, STATUS.SKIPPED].includes(data.status)) {
          // Track tutorial completion/skip event
          window.gtag('event', 'tutorial_completion', {
            'event_category': 'engagement',
            'event_label': data.status.toLowerCase(),
            'value': data.status === STATUS.FINISHED ? tutorialSteps.length : data.index + 1,
            'device_type': 'mobile',
            'steps_viewed': data.index + 1
          });
          onFinish();
        }
      }}
      tooltipComponent={({ continuous, index, step, backProps, closeProps, primaryProps, skipProps, tooltipProps }) => (
        <div
          {...tooltipProps}
          style={{
            ...tooltipProps.style,
            backgroundColor: theme.palette.background.paper,
            zIndex: 2000,
            boxShadow: theme.shadows[10],
            borderRadius: index === 0 ? 0 : theme.shape.borderRadius,
            padding: index === 0 ? 0 : theme.spacing(2),
            width: index === 0 ? '100vw' : 'auto',
            height: 'auto',
          }}
        >
          <div style={{ marginBottom: theme.spacing(2) }}>{step.content}</div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            gap: theme.spacing(1),
            padding: theme.spacing(2),
            paddingTop: 0,
            backgroundColor: theme.palette.background.paper
          }}>
            <button {...skipProps} style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: theme.shape.borderRadius,
              backgroundColor: 'transparent',
              color: theme.palette.text.secondary,
              cursor: 'pointer',
              fontSize: '0.875rem',
            }}>
              Skip tutorial
            </button>
            <div style={{ display: 'flex', gap: theme.spacing(1) }}>
              {index > 0 && (
                <button {...backProps} style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: theme.shape.borderRadius,
                  backgroundColor: 'transparent',
                  color: theme.palette.text.primary,
                  cursor: 'pointer',
                }}>
                  Back
                </button>
              )}
              <button {...primaryProps} style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: theme.shape.borderRadius,
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                cursor: 'pointer',
              }}>
                {index === tutorialSteps.length - 1 ? 'Done' : 'Next'}
              </button>
            </div>
          </div>
        </div>
      )}
      styles={{
        options: {
          zIndex: 2000,
          primaryColor: theme.palette.primary.main,
          overlayColor: 'rgba(0, 0, 0, 0.5)',
        }
      }}
    />
  );
};

const DesktopTutorial = ({ onFinish }) => {
  const theme = useTheme();

  const tutorialSteps = [
    {
      target: 'body',
      content: (
        <div style={{ 
          maxHeight: '90vh',
          overflowY: 'auto',
          width: '100%',
          padding: theme.spacing(2)
        }}>
          <h3 style={{ 
            color: theme.palette.primary.main, 
            marginTop: 0,
            marginBottom: theme.spacing(2)
          }}>
            Welcome to Writellect! 🎉
          </h3>
          <p style={{ marginBottom: theme.spacing(2) }}>
            You've been granted <strong>20 credits</strong> to start creating amazing content! With these credits, you can generate:
          </p>
          <ul style={{ marginBottom: theme.spacing(2), paddingLeft: theme.spacing(2) }}>
            <li>Up to ~133,000 words using GPT-4o-mini model</li>
            <li>Or ~20 images using Flux Pro 1.1 model</li>
          </ul>
          <p>During the free trial, you have access to non-premium models. For premium models(like Claude 3.5 Sonnet), you need to subscribe to a plan.</p>
          <p>
            Let's take a quick tour of the platform to help you get started.
          </p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
      styles: {
        options: {
          width: '500px',
          arrowColor: 'transparent'
        },
        tooltip: {
          width: '500px',
          maxWidth: '500px',
          minHeight: 'min-content',
          borderRadius: theme.shape.borderRadius,
          padding: 0,
          backgroundColor: theme.palette.background.paper
        }
      }
    },
    {
      target: '.project-selector',
      content: 'Select or create a project to manage your content. Each project can have its own resources, articles, and social media posts.',
      disableBeacon: true,
      placement: 'bottom'
    },
    {
      target: '.dashboard-link',
      content: 'The dashboard gives you an overview of your project\'s performance and recent activity.',
      placement: 'right'
    },
    {
      target: '[data-id="sources"]',
      content: 'The Sources section is where you manage your content sources and data streams.',
      placement: 'right',
      spotlightPadding: 5,
      disableBeacon: true
    },
    {
      target: '.articles-section',
      content: 'Create and manage your blog articles here.',
      placement: 'right'
    },
    {
      target: '.settings-section',
      content: 'Configure your project settings, integrations, and preferences.',
      placement: 'right'
    }
  ];

  return (
    <Joyride
      steps={tutorialSteps}
      run={true}
      continuous
      showProgress
      showSkipButton
      callback={(data) => {
        if ([STATUS.FINISHED, STATUS.SKIPPED].includes(data.status)) {
          // Track tutorial completion/skip event
          window.gtag('event', 'tutorial_completion', {
            'event_category': 'engagement',
            'event_label': data.status.toLowerCase(),
            'value': data.status === STATUS.FINISHED ? tutorialSteps.length : data.index + 1,
            'device_type': 'desktop',
            'steps_viewed': data.index + 1
          });
          onFinish();
        }
      }}
      tooltipComponent={({ continuous, index, step, backProps, closeProps, primaryProps, skipProps, tooltipProps }) => (
        <div
          {...tooltipProps}
          style={{
            ...tooltipProps.style,
            backgroundColor: theme.palette.background.paper,
            zIndex: 2000,
            boxShadow: theme.shadows[10],
            borderRadius: theme.shape.borderRadius,
            padding: theme.spacing(2),
            width: '500px',
            height: 'auto',
          }}
        >
          <div style={{ marginBottom: theme.spacing(2) }}>{step.content}</div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            gap: theme.spacing(1),
            padding: theme.spacing(2),
            paddingTop: 0,
            backgroundColor: theme.palette.background.paper
          }}>
            <button {...skipProps} style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: theme.shape.borderRadius,
              backgroundColor: 'transparent',
              color: theme.palette.text.secondary,
              cursor: 'pointer',
              fontSize: '0.875rem',
            }}>
              Skip tutorial
            </button>
            <div style={{ display: 'flex', gap: theme.spacing(1) }}>
              {index > 0 && (
                <button {...backProps} style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: theme.shape.borderRadius,
                  backgroundColor: 'transparent',
                  color: theme.palette.text.primary,
                  cursor: 'pointer',
                }}>
                  Back
                </button>
              )}
              <button {...primaryProps} style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: theme.shape.borderRadius,
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                cursor: 'pointer',
              }}>
                {index === tutorialSteps.length - 1 ? 'Done' : 'Next'}
              </button>
            </div>
          </div>
        </div>
      )}
      styles={{
        options: {
          zIndex: 2000,
          primaryColor: theme.palette.primary.main,
          overlayColor: 'rgba(0, 0, 0, 0.5)',
        }
      }}
    />
  );
};

const AppTutorial = () => {
  const [runTutorial, setRunTutorial] = useState(false);
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 600);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const hasSeenTutorial = localStorage.getItem('hasSeenAppTutorial');
    if (!hasSeenTutorial) {
      setRunTutorial(true);
    }
  }, [location.state]);

  const handleFinish = () => {
    localStorage.setItem('hasSeenAppTutorial', 'true');
    setRunTutorial(false);
  };

  if (!runTutorial) return null;

  return isMobile ? (
    <MobileTutorial onFinish={handleFinish} />
  ) : (
    <DesktopTutorial onFinish={handleFinish} />
  );
};

export default AppTutorial; 